import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import {
  nomMois, iso, nbJoursDuMois, jourSemaine, parseIso, aujourdhui, JOURS_COURTS, fetchAll,
} from '../../utils/planning'
import './AssistanatPlanning.css'
import Combobox from '../../components/Combobox'

function nomCourt(s) {
  return s ? `${s.prenom} ${(s.nom || '').charAt(0)}.` : '?'
}

function formatEuro(n) {
  return `${Number(n).toLocaleString('fr-FR')} €`
}

export default function AssistanatPlanning() {
  const { profile } = useAuth()
  const [mois, setMois] = useState(() => {
    const t = parseIso(aujourdhui())
    return { annee: t.annee, mois: t.mois }
  })
  const [sups, setSups] = useState([])
  const [besoins, setBesoins] = useState({})
  const [affs, setAffs] = useState([])
  const [dispos, setDispos] = useState({})
  const [tarif, setTarif] = useState(null)
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [occupe, setOccupe] = useState(false)

  const listeMois = useMemo(() => {
    const t = parseIso(aujourdhui())
    const res = []
    let a = t.annee
    let m = t.mois - 2
    if (m < 1) { m += 12; a -= 1 }
    for (let i = 0; i < 15; i++) {
      res.push({ annee: a, mois: m })
      m += 1
      if (m === 13) { m = 1; a += 1 }
    }
    return res
  }, [])

  const charger = useCallback(async () => {
    setLoading(true)
    const debut = iso(mois.annee, mois.mois, 1)
    const fin = iso(mois.annee, mois.mois, nbJoursDuMois(mois.annee, mois.mois))
    const [rSup, rB, rA, rD, rT] = await Promise.all([
      supabase.from('profiles').select('id, prenom, nom, actif').eq('role', 'superviseur').order('prenom'),
      fetchAll(() => supabase.from('besoins_assistanat').select('*').gte('date', debut).lte('date', fin).order('date')),
      fetchAll(() => supabase.from('affectations').select('*').eq('type', 'ASSISTANAT')
        .gte('date', debut).lte('date', fin).order('date').order('created_at')),
      fetchAll(() => supabase.from('dispos_assistanat').select('superviseur_id, date, dispo')
        .gte('date', debut).lte('date', fin).order('superviseur_id').order('date')),
      supabase.from('tarifs_journee').select('montant, date_effet').eq('code', 'ASSISTANAT')
        .lte('date_effet', fin).order('date_effet', { ascending: false }).limit(1),
    ])
    if (rSup.error || rB.error || rA.error || rD.error || rT.error) {
      setMsg({ type: 'error', text: 'Erreur lors du chargement du mois.' })
    }
    const mB = {}
    for (const b of rB.data) mB[b.date] = b
    const mD = {}
    for (const d of rD.data) mD[`${d.superviseur_id}|${d.date}`] = d.dispo
    setSups(rSup.data || [])
    setBesoins(mB)
    setAffs(rA.data)
    setDispos(mD)
    setTarif(rT.data && rT.data.length > 0 ? Number(rT.data[0].montant) : null)
    setLoading(false)
  }, [mois])

  useEffect(() => { charger() }, [charger])

  const supParId = useMemo(() => {
    const m = {}
    for (const s of sups) m[s.id] = s
    return m
  }, [sups])

  const jours = useMemo(() => {
    const n = nbJoursDuMois(mois.annee, mois.mois)
    const res = []
    for (let j = 1; j <= n; j++) {
      const date = iso(mois.annee, mois.mois, j)
      res.push({
        date,
        jsem: jourSemaine(date),
        besoin: besoins[date] || null,
        affectes: affs.filter(a => a.date === date && a.statut !== 'annule'),
        positionnes: sups.filter(s => dispos[`${s.id}|${date}`] === true),
      })
    }
    return res
  }, [mois, besoins, affs, sups, dispos])

  const stats = useMemo(() => {
    let requis = 0
    let affectes = 0
    let manquants = 0
    let nonPublies = 0
    let aValider = 0
    let montantValide = 0
    const t = aujourdhui()
    for (const j of jours) {
      const n = j.besoin ? j.besoin.nb_requis : 0
      requis += n
      affectes += j.affectes.length
      if (j.affectes.length < n) manquants += n - j.affectes.length
      for (const a of j.affectes) {
        if (!a.publie_le) nonPublies += 1
        if (a.statut === 'prevu' && a.date <= t) aValider += 1
        if (a.statut === 'valide') montantValide += Number(a.montant || 0)
      }
    }
    return { requis, affectes, manquants, nonPublies, aValider, montantValide }
  }, [jours])

  function erreur(texte) {
    setMsg({ type: 'error', text: texte })
  }

  async function changerBesoin(date, texte) {
    setMsg(null)
    const n = parseInt(texte, 10)
    const existant = besoins[date]
    if (!n || n < 1) {
      if (!existant) return
      if (affs.some(a => a.date === date)) { erreur('Des assistants sont déjà affectés ce jour : retire-les avant de supprimer le besoin.'); charger(); return }
      const { error } = await supabase.from('besoins_assistanat').delete().eq('date', date)
      if (error) { erreur("Erreur lors de l'enregistrement."); return }
      setBesoins(prev => { const c = { ...prev }; delete c[date]; return c })
      return
    }
    if (existant && existant.nb_requis === n) return
    const ligne = { date, nb_requis: n, commentaire: existant ? existant.commentaire : null }
    const { error } = await supabase.from('besoins_assistanat').upsert(ligne, { onConflict: 'date' })
    if (error) { erreur("Erreur lors de l'enregistrement."); return }
    setBesoins(prev => ({ ...prev, [date]: ligne }))
  }

  async function changerCours(a, texte) {
    const cours = texte.trim() || null
    if (cours === (a.cours || null)) return
    const { data, error } = await supabase.from('affectations').update({ cours }).eq('id', a.id).select()
    if (error || !data || data.length === 0) { erreur("Erreur lors de l'enregistrement du nom du cours."); return }
    setAffs(prev => prev.map(x => (x.id === a.id ? { ...x, cours } : x)))
  }

  async function changerCommentaire(date, texte) {
    const existant = besoins[date]
    if (!existant) return
    const commentaire = texte.trim() || null
    if (commentaire === (existant.commentaire || null)) return
    const { error } = await supabase.from('besoins_assistanat').update({ commentaire }).eq('date', date)
    if (error) { erreur("Erreur lors de l'enregistrement."); return }
    setBesoins(prev => ({ ...prev, [date]: { ...existant, commentaire } }))
  }

  async function ajouter(date, supId) {
    if (!supId) return
    setMsg(null)
    const { data, error } = await supabase.from('affectations')
      .insert({ date, type: 'ASSISTANAT', superviseur_id: supId, statut: 'prevu', cree_par: profile ? profile.id : null })
      .select().single()
    if (error) {
      erreur(error.code === '23505' ? 'Cet assistant est déjà affecté ce jour.' : "Erreur lors de l'affectation.")
      return
    }
    setAffs(prev => [...prev, data])
  }

  async function retirer(a) {
    if (a.statut === 'valide') { erreur('Journée validée : annule d\'abord la validation.'); return }
    setMsg(null)
    const { error } = await supabase.from('affectations').delete().eq('id', a.id)
    if (error) { erreur('Erreur lors du retrait.'); return }
    setAffs(prev => prev.filter(x => x.id !== a.id))
  }

  async function basculerValidation(a) {
    setMsg(null)
    if (a.statut === 'valide') {
      const patch = { statut: 'prevu', montant: null }
      const { error } = await supabase.from('affectations').update(patch).eq('id', a.id)
      if (error) { erreur('Erreur lors de la mise à jour.'); return }
      setAffs(prev => prev.map(x => (x.id === a.id ? { ...x, ...patch } : x)))
      return
    }
    if (a.date > aujourdhui()) { erreur("Cette journée n'a pas encore eu lieu."); return }
    if (tarif === null) { erreur('Aucun tarif Assistanat trouvé dans tarifs_journee.'); return }
    const patch = { statut: 'valide', montant: tarif, publie_le: a.publie_le || new Date().toISOString() }
    const { error } = await supabase.from('affectations').update(patch).eq('id', a.id)
    if (error) { erreur('Erreur lors de la validation.'); return }
    setAffs(prev => prev.map(x => (x.id === a.id ? { ...x, ...patch } : x)))
  }

  async function validerRealises() {
    if (tarif === null) { erreur('Aucun tarif Assistanat trouvé dans tarifs_journee.'); return }
    const t = aujourdhui()
    const ids = affs.filter(a => a.statut === 'prevu' && a.date <= t).map(a => a.id)
    if (ids.length === 0) return
    setMsg(null)
    setOccupe(true)
    let { error } = await supabase.from('affectations').update({ statut: 'valide', montant: tarif }).in('id', ids)
    if (!error) {
      // une journee validee doit etre visible du superviseur pour qu'il la voie dans Mes seances
      const r = await supabase.from('affectations').update({ publie_le: new Date().toISOString() }).in('id', ids).is('publie_le', null)
      error = r.error
    }
    setOccupe(false)
    if (error) { erreur('Erreur lors de la validation.'); return }
    setMsg({ type: 'success', text: `${ids.length} journée(s) validée(s) à ${formatEuro(tarif)}.` })
    charger()
  }

  function bornes() {
    return {
      debut: iso(mois.annee, mois.mois, 1),
      fin: iso(mois.annee, mois.mois, nbJoursDuMois(mois.annee, mois.mois)),
    }
  }

  async function publier() {
    setMsg(null)
    setOccupe(true)
    const { debut, fin } = bornes()
    const { error } = await supabase.from('affectations')
      .update({ publie_le: new Date().toISOString() })
      .eq('type', 'ASSISTANAT').gte('date', debut).lte('date', fin)
      .not('superviseur_id', 'is', null).is('publie_le', null)
    setOccupe(false)
    if (error) { erreur('Erreur lors de la publication.'); return }
    setMsg({ type: 'success', text: 'Assistanat du mois publié.' })
    charger()
  }

  function optionsPour(j) {
    const deja = new Set(j.affectes.map(a => a.superviseur_id))
    return sups.filter(s => s.actif && !deja.has(s.id)).map(s => {
      const d = dispos[`${s.id}|${j.date}`]
      const rang = d === true ? 0 : d === undefined ? 1 : 2
      const suffixe = d === true ? ' (dispo)' : d === false ? ' (NON)' : ''
      return { id: s.id, rang, label: `${s.prenom} ${s.nom}${suffixe}` }
    }).sort((a, b) => a.rang - b.rang || a.label.localeCompare(b.label, 'fr'))
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Assistanat de cours</h2>
          <div className="page-header-sub">
            Besoins du mois, positionnements des superviseurs et affectations
            {tarif !== null ? ` · ${formatEuro(tarif)} par journée` : ''}
          </div>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="as-bar">
        <select
          value={`${mois.annee}-${mois.mois}`}
          onChange={e => {
            const [a, m] = e.target.value.split('-').map(Number)
            setMois({ annee: a, mois: m })
          }}
        >
          {listeMois.map(m => (
            <option key={`${m.annee}-${m.mois}`} value={`${m.annee}-${m.mois}`}>{nomMois(m.mois)} {m.annee}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={validerRealises} disabled={occupe || stats.aValider === 0}>
            {`Valider les journées réalisées (${stats.aValider})`}
          </button>
          <button className="btn btn-bordeaux" onClick={publier} disabled={occupe || stats.nonPublies === 0}>
            {`Publier (${stats.nonPublies})`}
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="s-label">Postes requis</div>
          <div className="s-value">{stats.requis}</div>
          <div className="s-sub">{stats.affectes} affecté(s)</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Postes manquants</div>
          <div className="s-value text-bordeaux">{stats.manquants}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Montant validé</div>
          <div className="s-value">{formatEuro(stats.montantValide)}</div>
          <div className="s-sub">payé après validation</div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          <table className="as-table">
            <thead>
              <tr>
                <th>Jour</th>
                <th>Besoin</th>
                <th>Positionnés</th>
                <th>Affectés</th>
                <th>Ajouter</th>
              </tr>
            </thead>
            <tbody>
              {jours.map(j => {
                const requis = j.besoin ? j.besoin.nb_requis : 0
                const etat = !j.besoin && j.affectes.length === 0 ? '' : j.affectes.length < requis ? 'as-manque' : j.affectes.length > requis ? 'as-trop' : 'as-ok'
                return (
                  <tr key={j.date} className={`${j.jsem >= 5 ? 'as-weekend' : ''} ${etat}`}>
                    <td className="as-date">{JOURS_COURTS[j.jsem]} {parseIso(j.date).jour}</td>
                    <td>
                      <input
                        key={`b-${j.date}-${requis}`}
                        className="as-num"
                        type="number"
                        min="0"
                        defaultValue={requis || ''}
                        placeholder="0"
                        onBlur={e => changerBesoin(j.date, e.target.value)}
                      />
                      {j.besoin && (
                        <input
                          key={`c-${j.date}-${j.besoin.commentaire || ''}`}
                          className="as-com"
                          defaultValue={j.besoin.commentaire || ''}
                          placeholder="Commentaire"
                          onBlur={e => changerCommentaire(j.date, e.target.value)}
                        />
                      )}
                    </td>
                    <td>
                      {j.positionnes.map(s => (
                        <span key={s.id} className="as-chip as-chip-dispo">{nomCourt(s)}</span>
                      ))}
                    </td>
                    <td>
                      {j.affectes.map(a => {
                        const nonDispo = dispos[`${a.superviseur_id}|${j.date}`] === false
                        return (
                          <div key={a.id} className="as-affecte">
                          <span className={`as-chip ${a.statut === 'valide' ? 'as-chip-valide' : nonDispo ? 'as-chip-alerte' : 'as-chip-affecte'}`}>
                            {nomCourt(supParId[a.superviseur_id])}
                            {a.statut === 'valide' && ` · ${formatEuro(a.montant)}`}
                            {a.publie_le && <span className="as-pub" title="Publié"> ●</span>}
                            {a.date <= aujourdhui() && (
                              <button type="button" className="as-btn" title={a.statut === 'valide' ? 'Annuler la validation' : 'Valider la journée'} onClick={() => basculerValidation(a)}>
                                {a.statut === 'valide' ? '↺' : '✓'}
                              </button>
                            )}
                            <button type="button" className="as-btn" title="Retirer" onClick={() => retirer(a)}>✕</button>
                          </span>
                          <input
                            key={`k-${a.id}-${a.cours || ''}`}
                            className="as-com as-cours-input"
                            defaultValue={a.cours || ''}
                            placeholder="Nom du cours"
                            onBlur={e => changerCours(a, e.target.value)}
                          />
                          </div>
                        )
                      })}
                    </td>
                    <td>
                      {j.besoin && (
                        <Combobox
                          className="as-select"
                          value=""
                          resetAfterSelect
                          placeholder="Affecter..."
                          onChange={v => v && ajouter(j.date, v)}
                          options={optionsPour(j).map(o => ({ value: o.id, label: o.label }))}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
